import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Music, Plus, Clock, CheckCircle, AlertCircle,
  Headphones, FileText, Loader2, Star, Image, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";
import SongPlaylist from "@/components/dashboard/SongPlaylist";
import AbandonedCartBanner from "@/components/AbandonedCartBanner";

type SongOrder = Tables<"song_orders">;

const statusConfig: Record<string, { label: string; icon: typeof Clock; color: string }> = {
  submitted: { label: "Submitted", icon: FileText, color: "text-muted-foreground" },
  in_review: { label: "In Review", icon: Clock, color: "text-primary" },
  drafting_lyrics: { label: "Writing Lyrics", icon: FileText, color: "text-primary" },
  composing: { label: "Composing", icon: Music, color: "text-primary" },
  ready_for_review: { label: "Ready for Review", icon: AlertCircle, color: "text-accent" },
  revision_requested: { label: "Changes Requested", icon: Clock, color: "text-orange-500" },
  approved: { label: "Approved", icon: CheckCircle, color: "text-green-600" },
  completed: { label: "Completed", icon: Headphones, color: "text-green-600" },
};

const eventTypeLabels: Record<string, string> = {
  wedding: "Wedding", birthday: "Birthday", anniversary: "Anniversary",
  graduation: "Graduation", memorial: "Memorial", proposal: "Proposal",
  baby_shower: "Baby Shower", retirement: "Retirement", other: "Other",
};

const OrderCard = ({ order }: { order: SongOrder }) => {
  const cfg = statusConfig[order.status] || statusConfig.submitted;
  const StatusIcon = cfg.icon;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl shadow-soft p-6 border border-accent/20">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-foreground">
            {eventTypeLabels[order.event_type]} Song for {order.recipient_names}
          </h3>
          <p className="text-sm text-muted-foreground">
            Order #{order.order_number} · {order.plan_type?.charAt(0).toUpperCase() + order.plan_type?.slice(1)} Plan
          </p>
        </div>
        <Link to={`/order/${order.id}`} className="text-xs text-primary hover:underline whitespace-nowrap">
          View Full Details →
        </Link>
      </div>
      <div className="flex items-center gap-2">
        <StatusIcon className={`w-4 h-4 ${cfg.color}`} />
        <span className={`text-sm font-medium ${cfg.color}`}>{cfg.label}</span>
      </div>
    </motion.div>
  );
};
const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<SongOrder[]>([]);
  const [unpaidOrders, setUnpaidOrders] = useState<SongOrder[]>([]);
  const [dismissedUnpaid, setDismissedUnpaid] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) return;
      const [paidRes, unpaidRes] = await Promise.all([
        supabase.from("song_orders").select("*").eq("user_id", user.id).neq("status", "pending_payment").order("created_at", { ascending: false }),
        supabase.from("song_orders").select("*").eq("user_id", user.id).eq("status", "pending_payment" as any).order("created_at", { ascending: false }),
      ]);
      if (!paidRes.error && paidRes.data) setOrders(paidRes.data);
      if (!unpaidRes.error && unpaidRes.data) setUnpaidOrders(unpaidRes.data);
      setLoading(false);
    };
    if (user) fetchOrders();
  }, [user]);

  const handleOrderUpdate = (updated: SongOrder) => {
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
  };

  const handleDismissUnpaid = (orderId: string) => {
    setDismissedUnpaid((prev) => new Set(prev).add(orderId));
  };

  const visibleUnpaid = unpaidOrders.filter((o) => !dismissedUnpaid.has(o.id));
  const awaitingApproval = orders.filter(o => o.status === "ready_for_review" && !o.approved);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">My Dashboard</h1>
              <p className="text-muted-foreground">Track your song orders and listen to your creations.</p>
            </div>
            <Button variant="hero" onClick={() => setShowPlanDialog(true)}>
              <Plus className="w-5 h-5 mr-2" /> Create New Song
            </Button>
          </div>

          {/* Abandoned Cart Recovery */}
          {visibleUnpaid.length > 0 && (
            <div className="space-y-3 mb-8">
              <AnimatePresence>
                {visibleUnpaid.map((o) => (
                  <AbandonedCartBanner key={o.id} order={o} onDismiss={handleDismissUnpaid} />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><Music className="w-6 h-6 text-primary" /></div>
                <div><p className="text-2xl font-bold text-foreground">{orders.length}</p><p className="text-muted-foreground text-sm">Total Orders</p></div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center"><Clock className="w-6 h-6 text-accent" /></div>
                <div><p className="text-2xl font-bold text-foreground">{orders.filter(o => !["approved", "completed"].includes(o.status)).length}</p><p className="text-muted-foreground text-sm">In Progress</p></div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-card rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-600" /></div>
                <div><p className="text-2xl font-bold text-foreground">{orders.filter(o => o.status === "completed").length}</p><p className="text-muted-foreground text-sm">Completed</p></div>
              </div>
            </motion.div>
          </div>

          {/* Song Playlist */}
          <SongPlaylist orders={orders} />

          {/* Awaiting Approval Section */}
          {awaitingApproval.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-accent" />
                <h2 className="font-heading text-xl font-semibold text-foreground">
                  Awaiting Your Approval ({awaitingApproval.length})
                </h2>
              </div>
              <div className="space-y-4">
                {awaitingApproval.map(order => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            </div>
          )}

          {/* Orders List */}
          <div className="bg-card rounded-2xl shadow-soft overflow-hidden">
            <div className="p-6 border-b border-border">
              <h2 className="font-heading text-xl font-semibold text-foreground">Your Song Orders</h2>
            </div>

            {loading ? (
              <div className="p-12 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : orders.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4"><Music className="w-8 h-8 text-muted-foreground" /></div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">No songs yet</h3>
                <p className="text-muted-foreground mb-6">Start creating your first personalized song!</p>
                <Button variant="hero" asChild><Link to="/create">Create Your First Song</Link></Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {orders.map((order, index) => {
                  const status = statusConfig[order.status] || statusConfig.submitted;
                  const StatusIcon = status.icon;
                  return (
                    <motion.div key={order.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }} className="p-6 hover:bg-muted/30 transition-colors">
                      <Link to={`/order/${order.id}`} className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center"><Music className="w-6 h-6 text-primary" /></div>
                          <div>
                            <h3 className="font-semibold text-foreground">{eventTypeLabels[order.event_type]} Song</h3>
                            <p className="text-sm text-muted-foreground">For {order.recipient_names}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className={`flex items-center gap-2 ${status.color}`}>
                            <StatusIcon className="w-4 h-4" />
                            <span className="text-sm font-medium hidden sm:inline">{status.label}</span>
                          </div>
                          <span className="text-sm text-muted-foreground hidden md:inline">{new Date(order.created_at).toLocaleDateString()}</span>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Plan Selection Dialog */}
          <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-center">Choose Your Package</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {/* Essentials */}
                <button
                  onClick={() => { setShowPlanDialog(false); localStorage.removeItem("songFormData"); navigate("/create?plan=essentials"); }}
                  className="relative bg-card border border-border rounded-xl p-5 text-left hover:border-primary/50 hover:shadow-soft transition-all group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Music className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground">Essentials</h3>
                  </div>
                  <p className="text-2xl font-bold text-foreground mb-2">$35</p>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />Personalized song</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />Custom lyrics & music</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />Digital MP3 delivery</li>
                  </ul>
                </button>

                {/* Premium */}
                <button
                  onClick={() => { setShowPlanDialog(false); localStorage.removeItem("songFormData"); navigate("/create?plan=premium"); }}
                  className="relative bg-card border-2 border-primary rounded-xl p-5 text-left hover:shadow-glow transition-all group"
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" /> Most Popular
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Image className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground">Premium</h3>
                  </div>
                  <p className="text-2xl font-bold text-foreground mb-2">$85</p>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />Everything in Essentials</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />Slideshow video</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />Digital lyric sheet</li>
                  </ul>
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
